import { createElement, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { AssessmentDocumentViewModel } from '@features/assessment/types';
import type { SingularKeys } from '@libs/i18n';
import { useQueryClient } from '@tanstack/react-query';

import { DI_TOKENS } from '@constants';
import { DocumentClassificationBadge } from '@features/assessment/components/workflow/DocumentClassificationBadge';
import { useInjection } from '@hooks';
import {
  DocumentClassificationInvalidReason,
  DocumentClassificationStatus,
  FileStatus,
  LanguageCode,
} from '@libs/constants';
import { DocumentClassificationMetadataDto } from '@libs/types';
import { isTerminalClassificationStatus } from '@libs/utils';
import { AssessmentWorkflowStore } from '@stores';

import {
  useApplyClassificationCandidate,
  useMoveDocumentToSuggestedDomain,
} from '../api/assessment-documents-api';
import {
  fetchClassificationStatus,
  useClassifyDocuments,
} from '../api/document-classification-api';
import { ClassificationIssue } from '../components/workflow/ClassificationWarningModal';

const POLL_INTERVAL_MS = 5_000;
const POLL_TIMEOUT_MS = 5 * 60 * 1_000;

// The `errors` map holds i18n keys, not messages: a caught error can carry AI
// provider detail, so it goes to the console and never into the UI.
const CLASSIFICATION_FAILED_KEY: SingularKeys =
  'app.assessment.classification.processingFailed.description';
const CLASSIFICATION_TIMED_OUT_KEY: SingularKeys =
  'app.assessment.classification.processingFailed.timedOut';

const isInFlight = (s: DocumentClassificationStatus | null | undefined) =>
  s === DocumentClassificationStatus.PENDING ||
  s === DocumentClassificationStatus.PROCESSING;

const isVisibleFile = (file: AssessmentDocumentViewModel) =>
  file.status === FileStatus.UPLOADED || file.status === FileStatus.NOT_SAVED;

/**
 * Hook for managing document classification workflow.
 *
 * Classification is event-driven on the backend: `confirm-upload` seeds
 * `classificationMetadata = PENDING` and the AI worker transitions it to
 * PROCESSING → COMPLETED / FAILED. This hook only polls the status endpoint
 * and surfaces a live overlay so the badge can flip "Queued" → "AI is
 * analyzing your document…" without waiting for a domain refetch. The
 * explicit `POST /classify` endpoint is hit only when the user retries a
 * FAILED doc.
 */
export const useDocumentClassification = ({
  assessmentId,
  assessmentDomainId,
  language,
  filesArray,
  onClassificationSuccess,
  onDocumentMoved,
  onSaveAndNext,
}: {
  assessmentId: string;
  /** UUID of the domain currently being edited (source for moves). */
  assessmentDomainId?: string;
  language?: LanguageCode;
  filesArray: AssessmentDocumentViewModel[];
  onClassificationSuccess?: () => void;
  /** Called with the documentId after a successful move so the caller can
   * drop the item from the current domain immediately. */
  onDocumentMoved?: (documentId: string) => void;
  onSaveAndNext: () => void;
}) => {
  const queryClient = useQueryClient();
  const workflowStore = useInjection<AssessmentWorkflowStore>(
    DI_TOKENS.AssessmentWorkflowStore,
  );

  // Latest server-reported status per in-flight document. Presence in this
  // map = "we are polling this doc"; the value is the most recent status
  // returned by the status endpoint. Cleared on terminal / timeout / error.
  const [liveStatuses, setLiveStatuses] = useState<Map<string, DocumentClassificationStatus>>(
    new Map(),
  );
  const [errors, setErrors] = useState<Map<string, SingularKeys>>(new Map());

  const updateMap = <V,>(
    setMap: React.Dispatch<React.SetStateAction<Map<string, V>>>,
    id: string,
    value: V | null,
  ) => {
    setMap((prev) => {
      if (value === null) {
        if (!prev.has(id)) return prev;
        const next = new Map(prev);
        next.delete(id);
        return next;
      }
      if (prev.get(id) === value) return prev;
      const next = new Map(prev);
      next.set(id, value);
      return next;
    });
  };

  const setLiveStatus = useCallback(
    (id: string, status: DocumentClassificationStatus | null) =>
      updateMap(setLiveStatuses, id, status),
    [],
  );
  const setError = useCallback(
    (id: string, messageKey: SingularKeys | null) => updateMap(setErrors, id, messageKey),
    [],
  );

  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Dedupe: never run more than one poll loop per document.
  const polledRef = useRef(new Set<string>());

  const { mutateAsync: classifyMutation } = useClassifyDocuments();
  const { mutateAsync: moveMutation } = useMoveDocumentToSuggestedDomain();
  const { mutateAsync: applyCandidateMutation } = useApplyClassificationCandidate();

  // Documents with an in-flight "move to suggested domain" request. Drives the
  // per-document spinner/disabled state on the move button.
  const [movingIds, setMovingIds] = useState<Set<string>>(new Set());

  // Documents with an in-flight candidate switch. Tracked separately from
  // `movingIds`: a document can legitimately offer both actions at once, and
  // one being in flight should not disable the other's spinner state.
  const [applyingCandidateIds, setApplyingCandidateIds] = useState<Set<string>>(new Set());

  /**
   * Re-match a document to one of its scored candidate documents.
   *
   * `targetDomainKey` is passed straight through: a rerouted document is offered
   * candidates from two domains, and whether settling the match is a re-match or
   * a relocation is the API's call to make, not this hook's. Snackbars +
   * invalidation live in the mutation hook; this owns only the per-document
   * in-flight flag.
   */
  const applyCandidate = useCallback(
    async (documentId: string, standardDocumentKey: string, targetDomainKey?: string) => {
      if (!assessmentDomainId) return;
      setApplyingCandidateIds((prev) => new Set(prev).add(documentId));
      try {
        await applyCandidateMutation({
          assessmentId,
          assessmentDomainId,
          documentId,
          standardDocumentKey,
          targetDomainKey,
        });
      } catch {
        // Error message is shown by the mutation's onError handler.
      } finally {
        if (isMountedRef.current) {
          setApplyingCandidateIds((prev) => {
            if (!prev.has(documentId)) return prev;
            const next = new Set(prev);
            next.delete(documentId);
            return next;
          });
        }
      }
    },
    [assessmentId, assessmentDomainId, applyCandidateMutation],
  );

  /**
   * Move a wrong-domain document to its AI-suggested domain. The success
   * snackbar + query invalidation are handled by the mutation hook; errors are
   * surfaced there too, so we only own the per-document in-flight flag here.
   */
  const moveDocument = useCallback(
    async (documentId: string) => {
      if (!assessmentDomainId) return;
      setMovingIds((prev) => new Set(prev).add(documentId));
      try {
        await moveMutation({ assessmentId, assessmentDomainId, documentId });
        // Drop the item from this domain right away — don't wait for the
        // background refetch (which can briefly re-flag it as "preparing").
        onDocumentMoved?.(documentId);
      } catch {
        // Error message is shown by the mutation's onError handler.
      } finally {
        if (isMountedRef.current) {
          setMovingIds((prev) => {
            if (!prev.has(documentId)) return prev;
            const next = new Set(prev);
            next.delete(documentId);
            return next;
          });
        }
      }
    },
    [assessmentId, assessmentDomainId, moveMutation, onDocumentMoved],
  );

  /**
   * Poll `GET /document-classifications` for one document until it reaches
   * a terminal state, times out, or the component unmounts. Owns the
   * `liveStatuses`/`errors` lifecycle for the doc.
   */
  const pollDocumentClassification = useCallback(
    async (documentId: string) => {
      const startTime = Date.now();
      // Seed the overlay so the spinner renders the right text on the very
      // first paint, before the first network response lands.
      setLiveStatus(documentId, DocumentClassificationStatus.PENDING);
      setError(documentId, null);

      try {
        while (true) {
          const res = await fetchClassificationStatus([documentId]);
          if (!isMountedRef.current) return;
          const doc = res.documents.find((d) => d.id === documentId);
          const status = doc?.classificationStatus;

          if (status && isTerminalClassificationStatus(status)) {
            setLiveStatus(documentId, null);
            if (status === DocumentClassificationStatus.FAILED) {
              setError(documentId, CLASSIFICATION_FAILED_KEY);
            } else {
              setError(documentId, null);
            }
            await queryClient.invalidateQueries({
              queryKey: ['assessments', assessmentId, 'domains'],
            });
            onClassificationSuccess?.();
            return;
          }

          if (status) setLiveStatus(documentId, status);

          if (Date.now() - startTime > POLL_TIMEOUT_MS) {
            setLiveStatus(documentId, null);
            setError(documentId, CLASSIFICATION_TIMED_OUT_KEY);
            return;
          }

          await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
          if (!isMountedRef.current) return;
        }
      } catch (error) {
        if (!isMountedRef.current) return;
        setLiveStatus(documentId, null);
        setError(documentId, CLASSIFICATION_FAILED_KEY);
        console.error('Document classification polling failed:', error);
      }
    },
    [assessmentId, onClassificationSuccess, queryClient, setError, setLiveStatus],
  );

  /**
   * Retry classification for a doc that previously ended in FAILED. The
   * only caller of `POST /document-classifications` — first-upload
   * classification is auto-triggered server-side.
   */
  const retryClassification = useCallback(
    async (documentId: string) => {
      // Show spinner immediately — before the POST resolves — so the user
      // sees feedback and doesn't click retry again (which the backend rejects
      // as a duplicate in-flight submission).
      setLiveStatus(documentId, DocumentClassificationStatus.PENDING);
      setError(documentId, null);
      polledRef.current.delete(documentId);

      try {
        const accepted = await classifyMutation({ documentIds: [documentId], language });
        const id = accepted.documentIds[0];
        polledRef.current.add(id);
        await pollDocumentClassification(id);
      } catch (error) {
        if (!isMountedRef.current) return;
        setLiveStatus(documentId, null);
        setError(documentId, CLASSIFICATION_FAILED_KEY);
        console.error('Document classification retry failed:', error);
      }
    },
    [classifyMutation, language, pollDocumentClassification, setError, setLiveStatus],
  );

  // Kick off polling for any visible file whose server-side classification
  // is still in flight (or whose metadata isn't seeded yet, which happens
  // right after upload).
  useEffect(() => {
    filesArray.forEach((file) => {
      if (!isVisibleFile(file)) return;
      const meta = file.classificationMetadata;
      const shouldPoll = !meta || isInFlight(meta.classificationStatus);
      if (!shouldPoll) return;
      if (polledRef.current.has(file.id)) return;
      polledRef.current.add(file.id);
      pollDocumentClassification(file.id);
    });
  }, [filesArray, pollDocumentClassification]);

  const duplicateKeys = useMemo(() => {
    const counts = new Map<string, number>();
    filesArray.forEach((f) => {
      const key = f.classificationMetadata?.matchedStandardDocumentKey;
      if (key && !f.deleted && isVisibleFile(f)) {
        counts.set(key, (counts.get(key) || 0) + 1);
      }
    });
    return new Set(
      Array.from(counts.entries())
        .filter(([, count]) => count > 1)
        .map(([key]) => key),
    );
  }, [filesArray]);

  /**
   * Standard document keys claimed by each document, keyed by document id.
   *
   * The candidate picker needs "which keys are taken by *someone else*", which
   * differs per row, so the full map is built once here and each row subtracts
   * its own key when rendering.
   */
  const matchedKeyByDocumentId = useMemo(() => {
    const map = new Map<string, string>();
    filesArray.forEach((f) => {
      const key = f.classificationMetadata?.matchedStandardDocumentKey;
      if (key && !f.deleted && isVisibleFile(f)) map.set(f.id, key);
    });
    return map;
  }, [filesArray]);

  /**
   * Render the classification badge. The live overlay (`liveStatuses`) is
   * the source of truth for the in-flight status — the view model's
   * `classificationMetadata` only refreshes on terminal (via query
   * invalidation), so without this overlay the badge would stay stuck on
   * the initial PENDING text the whole time the AI is actually PROCESSING.
   *
   * The overlay also covers the just-after-upload case where the view
   * model has no `classificationMetadata` at all: we synthesize a minimal
   * metadata object carrying the live status so the badge can still pick
   * the right "Queued" / "AI is analyzing…" message.
   */
  const renderClassificationBadge = useCallback<
    (file: AssessmentDocumentViewModel) => ReactNode
  >(
    (file) => {
      if (!isVisibleFile(file)) return null;

      const liveStatus = liveStatuses.get(file.id);
      const baseMetadata = file.classificationMetadata ?? null;

      const metadata: DocumentClassificationMetadataDto | null = liveStatus
        ? ({ ...(baseMetadata ?? {}), classificationStatus: liveStatus } as DocumentClassificationMetadataDto)
        : baseMetadata;

      const effectiveStatus = liveStatus ?? baseMetadata?.classificationStatus;
      const key = baseMetadata?.matchedStandardDocumentKey;

      // Keys held by every *other* document in this domain. A candidate in this
      // set is badged as taken and cannot be picked — choosing it would put two
      // documents on one standard document, which the API rejects anyway.
      const claimedDocumentKeys = new Set<string>();
      matchedKeyByDocumentId.forEach((matchedKey, documentId) => {
        if (documentId !== file.id) claimedDocumentKeys.add(matchedKey);
      });

      return createElement(DocumentClassificationBadge, {
        classificationMetadata: metadata,
        isClassifying: isInFlight(effectiveStatus),
        classificationErrorKey: errors.get(file.id),
        onRetry: () => retryClassification(file.id),
        isDuplicate: key ? duplicateKeys.has(key) : false,
        // Only offer the move action when we know the source domain. The badge
        // itself only renders the button for wrong-domain docs with a suggestion.
        onMove: assessmentDomainId ? () => moveDocument(file.id) : undefined,
        isMoving: movingIds.has(file.id),
        onApplyCandidate: assessmentDomainId
          ? (standardDocumentKey: string, targetDomainKey?: string) =>
              applyCandidate(file.id, standardDocumentKey, targetDomainKey)
          : undefined,
        isApplyingCandidate: applyingCandidateIds.has(file.id),
        claimedDocumentKeys,
      });
    },
    [
      liveStatuses,
      errors,
      retryClassification,
      duplicateKeys,
      assessmentDomainId,
      moveDocument,
      movingIds,
      applyCandidate,
      applyingCandidateIds,
      matchedKeyByDocumentId,
    ],
  );

  // ============================================================================
  // CLASSIFICATION WARNING MODAL
  // ============================================================================

  const classificationIssues = useMemo<ClassificationIssue[]>(() => {
    const issues: ClassificationIssue[] = [];
    filesArray.forEach((file) => {
      if (!isVisibleFile(file) || file.deleted) return;
      const status = file.classificationMetadata?.classificationStatus;

      if (status === DocumentClassificationStatus.COMPLETED) {
        const reason = file.classificationMetadata?.invalidReason;
        // WRONG_FORMAT is intentionally absent: the document matched and is
        // still submitted, so it is a warning on the card, not a blocking issue.
        if (
          reason === DocumentClassificationInvalidReason.NO_MATCH ||
          reason === DocumentClassificationInvalidReason.WRONG_DOMAIN ||
          reason === DocumentClassificationInvalidReason.UNSUPPORTED_FORMAT
        ) {
          issues.push({ fileName: file.fileName, reason: 'mismatch' });
        }
      } else if (errors.has(file.id) || status === DocumentClassificationStatus.FAILED) {
        issues.push({ fileName: file.fileName, reason: 'failed' });
      } else if (!file.classificationMetadata && !liveStatuses.has(file.id)) {
        // No metadata and not actively polling — legacy/unclassified doc.
        // PENDING/PROCESSING is handled via the `isAnyClassifying` path.
        issues.push({ fileName: file.fileName, reason: 'unclassified' });
      }
    });
    return issues;
  }, [filesArray, errors, liveStatuses]);

  const handleSaveWithClassificationCheck = useCallback(() => {
    if (classificationIssues.length > 0) {
      workflowStore.openClassificationWarning();
      return;
    }
    onSaveAndNext();
  }, [classificationIssues, onSaveAndNext, workflowStore]);

  const handleClassificationWarningSaveAnyway = useCallback(() => {
    workflowStore.closeClassificationWarning();
    onSaveAndNext();
  }, [onSaveAndNext, workflowStore]);

  const handleClassificationWarningCancel = useCallback(() => {
    workflowStore.closeClassificationWarning();
  }, [workflowStore]);

  useEffect(() => {
    workflowStore.registerClassificationWarningHandlers({
      issues: classificationIssues,
      onSaveAnyway: handleClassificationWarningSaveAnyway,
      onCancel: handleClassificationWarningCancel,
    });
    return () => workflowStore.registerClassificationWarningHandlers(null);
  }, [
    workflowStore,
    classificationIssues,
    handleClassificationWarningSaveAnyway,
    handleClassificationWarningCancel,
  ]);

  return {
    renderClassificationBadge,
    /** True while a move-to-suggested-domain request is in flight for the doc. */
    isDocumentMoving: (documentId: string) => movingIds.has(documentId),
    /**
     * Still queued for, or undergoing, AI analysis.
     *
     * A file can be fully uploaded and yet not finished — to the user that
     * still reads as loading, so the upload list groups it with in-flight
     * uploads rather than with completed ones. Prefers the live poll status,
     * which updates before the domain refetches.
     */
    isDocumentProcessing: (documentId: string) => {
      const live = liveStatuses.get(documentId);
      if (live) return isInFlight(live);
      const file = filesArray.find((f) => f.id === documentId);
      return isInFlight(file?.classificationMetadata?.classificationStatus);
    },
    classificationIssues,
    isClassificationWarningOpen: workflowStore.isClassificationWarningOpen,
    handleSaveWithClassificationCheck,
    handleClassificationWarningSaveAnyway,
    handleClassificationWarningCancel,
    // OR with the server-reported in-flight status so the Save & Next
    // gating reads `classifying` on a page refresh before the poll effect
    // has populated `liveStatuses`.
    isAnyClassifying:
      liveStatuses.size > 0 ||
      filesArray.some((f) => isInFlight(f.classificationMetadata?.classificationStatus)),
  };
};
